// How one notification reads in the drawer: its heading, which icon sits beside
// it and where clicking it goes. Pure, so it runs under Jest — the drawer itself
// reaches Firebase through NotificationContext and cannot.
//
// The lookup is deliberately open at runtime. The backend writes types this
// branch's union has never contained (`earnings_added` is already in production
// today), so an unknown type has to land on a neutral fallback rather than an
// empty row.

import type { NotificationType } from '../types/notification';

export type NotificationIcon =
  | 'video'
  | 'calendar-clock'
  | 'calendar-check'
  | 'calendar-x'
  | 'gavel'
  | 'wallet'
  | 'alert'
  | 'credit-card'
  | 'gift'
  | 'bell';

export interface NotificationDisplay {
  title: string;
  icon: NotificationIcon;
  /** An in-app route to open on click, or null when there is nowhere useful to go. */
  destination: string | null;
}

interface Entry {
  title: string;
  icon: NotificationIcon;
  destination: string | null;
}

/**
 * Keyed by `NotificationType` so TypeScript proves every union member has an
 * entry; read through a widened index signature so an unknown string simply
 * misses instead of throwing.
 */
const KNOWN: Record<NotificationType, Entry> = {
  call_ready: { title: 'Your call is ready', icon: 'video', destination: null },
  call_reminder: { title: 'Call reminder', icon: 'calendar-clock', destination: null },
  outbid: { title: 'You were outbid', icon: 'gavel', destination: null },
  booking_confirmed: { title: 'Booking confirmed', icon: 'calendar-check', destination: null },
  booking_cancelled: { title: 'Booking cancelled', icon: 'calendar-x', destination: null },
  payment_received: { title: 'Payment received', icon: 'wallet', destination: null },
  payment_failed: { title: 'Payment failed', icon: 'alert', destination: null },
  // Topping up is the only thing that unblocks the fan, which is what the
  // backend's own message tells them to do.
  subscription_cancelled_low_balance: {
    title: 'Subscription cancelled',
    icon: 'credit-card',
    destination: '/buy-credits',
  },
  gift_received: { title: 'Gift received', icon: 'gift', destination: '/payouts' },
};

export const FALLBACK_TITLE = 'Notification';

/**
 * The stored title wins: both backend writers set a good one, and keeping it
 * means every notification already on screen reads exactly as it does today.
 * The table's title is only the fallback for a document without one.
 */
export function notificationDisplay(notification: {
  type?: unknown;
  title?: unknown;
}): NotificationDisplay {
  const type = typeof notification.type === 'string' ? notification.type : '';
  // `hasOwnProperty`, not a bare index: a document typed `toString` would
  // otherwise pick up Object.prototype's member and count as known.
  const entry = Object.prototype.hasOwnProperty.call(KNOWN, type)
    ? (KNOWN as Record<string, Entry>)[type]
    : undefined;
  const stored = typeof notification.title === 'string' ? notification.title.trim() : '';

  return {
    title: stored || entry?.title || FALLBACK_TITLE,
    icon: entry?.icon ?? 'bell',
    destination: entry?.destination ?? null,
  };
}
